import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { HiPlus, HiFunnel } from "react-icons/hi2";
import Tooltip from "./Tooltip";

// Parse UTC timestamp from backend, ensuring it's treated as UTC
const parseUtcDate = (dateString) => {
  if (!dateString) return null;

  let dateStr = dateString;
  if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
    dateStr = dateStr + 'Z';
  } else if (!dateStr.includes('T') && !dateStr.endsWith('Z')) {
    dateStr = dateStr + 'Z';
  }

  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

// Format relative time for next scheduled run
const formatNextRun = (dateString) => {
  const date = parseUtcDate(dateString);
  if (!date) return null;

  const now = new Date();
  const diffMs = date - now;

  if (diffMs <= 0) return null;

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return `in ${diffMins}m`;
  if (diffHours < 24) return `in ${diffHours}h`;
  return `in ${diffDays}d`;
};

// Format UTC timestamp to local display: "Today 3:00 PM", "Tomorrow 9:00 AM", "Wed 9:00 AM"
const formatUpcomingTime = (dateString) => {
  const date = parseUtcDate(dateString);
  if (!date) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((target - today) / 86400000);

  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (dayDiff === 0) return `Today ${time}`;
  if (dayDiff === 1) return `Tomorrow ${time}`;
  if (dayDiff < 7) {
    const day = date.toLocaleDateString([], { weekday: 'short' });
    return `${day} ${time}`;
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ` ${time}`;
};

// Convert cron expression to readable interval label
const formatCronLabel = (cronExpression) => {
  if (!cronExpression) return '';
  const parts = cronExpression.split(' ');

  if (parts[0].startsWith('*/')) {
    const mins = parseInt(parts[0].substring(2));
    return mins < 60 ? `Every ${mins}m` : `Every ${Math.floor(mins / 60)}h`;
  }
  if (parts[0] === '0' && parts[1] === '*') return 'Every 1h';
  if (parts[0] === '0' && parts[1].startsWith('*/')) {
    return `Every ${parts[1].substring(2)}h`;
  }
  if (parts[0] === '0' && parts[1] === '0' && parts[2] === '*') return 'Daily';
  if (parts[0] === '0' && parts[1] !== '*' && parts[2] === '*') {
    return `Daily`;
  }
  if (parts[4] === '1-5') return 'Weekdays';
  if (parts[2] === '1' && parts[3] === '*') return 'Monthly';
  return 'Custom';
};

// Derive all status data from summary + liveStats
const getStatusData = (summary, liveStats) => {
  const running = summary?.running_count ?? liveStats?.running ?? 0;
  const queued = summary?.queued_count ?? liveStats?.queued ?? 0;
  const todayRuns = summary?.total_runs_today ?? 0;
  const failedToday = summary?.failed_runs_today ?? 0;
  const scheduledCount = summary?.scheduled_count ?? 0;
  const nextRunFormatted = formatNextRun(summary?.next_scheduled_run);
  const isActive = running > 0 || queued > 0;
  const hasSchedule = scheduledCount > 0;

  // Build tooltip text
  const parts = [];
  if (running > 0) parts.push(`${running} running`);
  if (queued > 0) parts.push(`${queued} queued`);
  const todayPart = `${todayRuns} run${todayRuns !== 1 ? 's' : ''} today`;
  parts.push(failedToday > 0 ? `${todayPart} (${failedToday} failed)` : todayPart);
  if (scheduledCount > 0) {
    const sched = `${scheduledCount} scheduled`;
    parts.push(nextRunFormatted ? `${sched}, next ${nextRunFormatted}` : sched);
  }
  const tooltip = parts.join('  ·  ');

  return { running, queued, todayRuns, failedToday, scheduledCount, nextRunFormatted, isActive, hasSchedule, tooltip };
};

// ── Scheduled Popover ──────────────────────────────────────────────
// Shows a list of upcoming scheduled tasks, anchored to the schedule section.
const ScheduledPopover = ({ upcomingScheduled, onClose, anchorRef }) => {
  const panelRef = useRef(null);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (anchorRef?.current && panelRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const panelRect = panelRef.current.getBoundingClientRect();

      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;

      let top;
      if (spaceBelow < panelRect.height + 16 && spaceAbove > spaceBelow) {
        top = anchorRect.top - panelRect.height - 8;
      } else {
        top = anchorRect.bottom + 8;
      }

      let left = anchorRect.left + anchorRect.width / 2 - panelRect.width / 2;
      if (left < 16) left = 16;
      left = Math.min(left, window.innerWidth - panelRect.width - 16);

      setPosition({ top, left });
    }
  }, [anchorRef]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[60]" onClick={onClose} />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed z-[70] w-72 rounded-xl shadow-2xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-gray-900 overflow-hidden"
        style={position ? { top: position.top, left: position.left } : { top: -9999, left: -9999 }}
      >
        {/* Header */}
        <div className="px-3 pt-2.5 pb-1.5 border-b border-gray-100 dark:border-white/[0.06]">
          <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Upcoming Scheduled
          </span>
        </div>

        {/* Task list */}
        <div className="py-1 max-h-64 overflow-y-auto">
          {upcomingScheduled.map(task => (
            <div key={task.id} className="flex items-start gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                  {task.name}
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {formatUpcomingTime(task.next_scheduled_run)}
                  </span>
                  {task.cron_expression && (
                    <>
                      <span className="text-[10px] text-gray-300 dark:text-gray-600">·</span>
                      <span className="text-[10px] text-blue-500 dark:text-blue-400 font-medium">
                        {formatCronLabel(task.cron_expression)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5">
                {formatNextRun(task.next_scheduled_run)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </>,
    document.body
  );
};

// ── Status Island (Desktop) ────────────────────────────────────────
// Compact pill with colored dots. Tooltip for full labels.
const StatusIsland = ({ summary, liveStats, upcomingScheduled = [] }) => {
  const { running, queued, todayRuns, failedToday, scheduledCount, nextRunFormatted, isActive, hasSchedule, tooltip } = getStatusData(summary, liveStats);
  const [showScheduled, setShowScheduled] = useState(false);
  const scheduleRef = useRef(null);

  const toggleScheduled = useCallback((e) => {
    e.stopPropagation();
    setShowScheduled(prev => !prev);
  }, []);

  const closeScheduled = useCallback(() => setShowScheduled(false), []);

  return (
    <div className="hidden md:block flex-shrink-0">
      <Tooltip content={showScheduled ? null : tooltip} position="bottom" borderColor={isActive ? '#f59e0b' : '#6b7280'}>
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full transition-all duration-300 ${
          isActive
            ? 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-500/20'
            : 'bg-gray-100 dark:bg-white/[0.03] border border-gray-200/60 dark:border-white/[0.06]'
        }`}>
          {/* Running */}
          {(isActive || running > 0) && (
            <div className="flex items-center gap-1">
              <div className="relative flex h-1.5 w-1.5 flex-shrink-0">
                {running > 0 && <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />}
                <div className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                  running > 0 ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
                }`} />
              </div>
              <span className={`text-xs font-medium tabular-nums ${
                running > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-600'
              }`}>{running}</span>
            </div>
          )}

          {/* Queued */}
          {(isActive || queued > 0) && (
            <div className="flex items-center gap-1">
              <div className={`w-1.5 h-1.5 rounded-full ${
                queued > 0 ? 'bg-orange-400' : 'bg-gray-300 dark:bg-gray-600'
              }`} />
              <span className={`text-xs font-medium tabular-nums ${
                queued > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400 dark:text-gray-600'
              }`}>{queued}</span>
            </div>
          )}

          {/* Divider before today */}
          {isActive && <div className="w-px h-3 bg-gray-300 dark:bg-gray-700/50" />}

          {/* Today's runs */}
          <div className="flex items-center gap-1">
            <span className={`text-xs font-medium tabular-nums ${
              todayRuns > 0 ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600'
            }`}>{todayRuns}</span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">today</span>
            {failedToday > 0 && (
              <span className="text-[10px] font-medium text-rose-500 dark:text-rose-400 tabular-nums">
                ({failedToday})
              </span>
            )}
          </div>

          {/* Scheduled (clickable) */}
          {hasSchedule && (
            <>
              <div className="w-px h-3 bg-gray-300 dark:bg-gray-700/50" />
              <div
                ref={scheduleRef}
                onClick={toggleScheduled}
                className="flex items-center gap-1 cursor-pointer rounded-full px-1 -mx-1 hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-colors"
              >
                <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400 tabular-nums">{scheduledCount}</span>
                {nextRunFormatted && (
                  <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                    {nextRunFormatted}
                  </span>
                )}
                <svg className={`w-2.5 h-2.5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${showScheduled ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </>
          )}
        </div>
      </Tooltip>

      {showScheduled && upcomingScheduled.length > 0 && (
        <ScheduledPopover
          upcomingScheduled={upcomingScheduled}
          onClose={closeScheduled}
          anchorRef={scheduleRef}
        />
      )}
    </div>
  );
};

// ── Mobile Status Pills ────────────────────────────────────────────
// Compact pills with text labels (no hover tooltips on touch).
const MobileStatusPills = ({ summary, liveStats, upcomingScheduled = [] }) => {
  const { running, queued, hasSchedule, nextRunFormatted, isActive } = getStatusData(summary, liveStats);
  const [showScheduled, setShowScheduled] = useState(false);
  const scheduleRef = useRef(null);

  if (!isActive && !(hasSchedule && nextRunFormatted)) return null;

  return (
    <div className="flex md:hidden items-center gap-2 mb-1.5 px-0.5 flex-wrap">
      {running > 0 && (
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-500/20">
          <div className="relative flex h-1.5 w-1.5 flex-shrink-0">
            <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <div className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-500" />
          </div>
          <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400 tabular-nums">{running} running</span>
        </div>
      )}
      {queued > 0 && (
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-950/20 border border-orange-200/60 dark:border-orange-500/20">
          <div className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
          <span className="text-[11px] font-medium text-orange-600 dark:text-orange-400 tabular-nums">{queued} queued</span>
        </div>
      )}
      {hasSchedule && nextRunFormatted && (
        <span
          ref={scheduleRef}
          onClick={() => upcomingScheduled.length > 0 && setShowScheduled(prev => !prev)}
          className="inline-flex items-center gap-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400 cursor-pointer"
        >
          Next {nextRunFormatted}
          <svg className={`w-2.5 h-2.5 transition-transform duration-200 ${showScheduled ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      )}
      {showScheduled && upcomingScheduled.length > 0 && (
        <ScheduledPopover
          upcomingScheduled={upcomingScheduled}
          onClose={() => setShowScheduled(false)}
          anchorRef={scheduleRef}
        />
      )}
    </div>
  );
};

// ── Search Input ───────────────────────────────────────────────────
// Manages its own local state so typing never re-renders the parent.
// Debounces onChange to parent after 200ms of inactivity.
const SearchInput = ({ searchQuery, onSearchChange }) => {
  const [localValue, setLocalValue] = useState(searchQuery);
  const timerRef = useRef(null);

  // Sync from parent when it clears externally (e.g. "Clear filters")
  useEffect(() => {
    if (!searchQuery && localValue) setLocalValue('');
  }, [searchQuery]);

  const handleChange = (e) => {
    const val = e.target.value;
    setLocalValue(val);
    clearTimeout(timerRef.current);
    if (!val.trim()) {
      onSearchChange('');
    } else {
      timerRef.current = setTimeout(() => onSearchChange(val), 400);
    }
  };

  const handleClear = () => {
    setLocalValue('');
    clearTimeout(timerRef.current);
    onSearchChange('');
  };

  return (
    <div className="w-full md:w-auto md:min-w-[160px] lg:min-w-[180px] relative">
      <svg className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3.5 h-3.5 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="text"
        placeholder="Search automations..."
        value={localValue}
        onChange={handleChange}
        className="w-full pl-8 pr-7 py-1.5 bg-gray-100 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-full text-xs text-gray-700 dark:text-gray-300
                   placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 focus:bg-white dark:focus:bg-white/[0.05]
                   transition-all duration-200 hover:border-gray-300 dark:hover:border-white/[0.1] hover:bg-gray-50 dark:hover:bg-white/[0.04]"
      />
      {localValue && (
        <button
          onClick={handleClear}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

// ── Timeline Controls ──────────────────────────────────────────────
const TimelineControls = ({ timeRange, onTimeRangeChange, scheduledOnly, onScheduledOnlyChange }) => {
  const timeRangeOptions = [
    { value: "hourly", label: "1h", description: "Last hour" },
    { value: "daily", label: "24h", description: "Today" },
    { value: "weekly", label: "7d", description: "This week" },
    { value: "monthly", label: "30d", description: "This month" },
  ];

  const getTimeRangeDescription = () => {
    switch (timeRange) {
      case "hourly": return "Last 60 minutes";
      case "daily": return "Last 24 hours";
      case "weekly": return "Last 7 days";
      case "monthly": return "Last 30 days";
      default: return "";
    }
  };

  return (
    <>
      {/* Mobile label */}
      <div className="md:hidden text-[11px] text-gray-600 dark:text-gray-500 font-medium">
        {getTimeRangeDescription()}
      </div>

      {/* Filter + Time range selector */}
      <div className="flex items-center gap-2">
        {/* Filter selector */}
        <div className="inline-flex items-center gap-0.5 bg-gray-100 dark:bg-white/[0.03] rounded-full p-0.5 border border-gray-200 dark:border-white/[0.06]">
          <button
            onClick={() => onScheduledOnlyChange?.(false)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all duration-200
              ${!scheduledOnly
                ? "bg-white dark:bg-white/[0.08] text-gray-800 dark:text-gray-100 shadow-sm border border-gray-200 dark:border-white/[0.1]"
                : "text-gray-600 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/50 border border-transparent"}`}
          >
            All
          </button>
          <button
            onClick={() => onScheduledOnlyChange?.(true)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all duration-200
              ${scheduledOnly
                ? "bg-white dark:bg-white/[0.08] text-gray-800 dark:text-gray-100 shadow-sm border border-gray-200 dark:border-white/[0.1]"
                : "text-gray-600 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/50 border border-transparent"}`}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="hidden sm:inline">Scheduled</span>
          </button>
        </div>

        {/* Time range selector */}
        <div className="inline-flex items-center gap-0.5 bg-gray-100 dark:bg-white/[0.03] rounded-full p-0.5 border border-gray-200 dark:border-white/[0.06]">
          {timeRangeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => onTimeRangeChange(option.value)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all duration-200
                ${timeRange === option.value
                  ? "bg-white dark:bg-white/[0.08] text-gray-800 dark:text-gray-100 shadow-sm border border-gray-200 dark:border-white/[0.1]"
                  : "text-gray-600 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700/50 border border-transparent"}`}
              title={option.description}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

// ── Create Button ──────────────────────────────────────────────────
const CreateButton = ({ onCreateNew }) => {
  if (!onCreateNew) return null;
  return (
    <button
      onClick={onCreateNew}
      className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 bg-[length:200%_100%] hover:bg-[position:right_center] text-white rounded-full shadow-md shadow-cyan-500/25 hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-300 flex-shrink-0"
    >
      <HiPlus className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">New</span>
    </button>
  );
};

// ── Filter Button ─────────────────────────────────────────────────
const FilterButton = ({ activeFilterCount, filterButtonRef, onToggleFilters }) => (
  <button
    ref={filterButtonRef}
    onClick={onToggleFilters}
    className={`relative p-1.5 rounded-full border transition-all duration-200
      ${activeFilterCount > 0
        ? 'bg-cyan-50 dark:bg-cyan-950/20 border-cyan-300 dark:border-cyan-500/30 text-cyan-600 dark:text-cyan-400'
        : 'bg-gray-100 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-white/[0.06] hover:text-gray-600 dark:hover:text-gray-300'
      }`}
    title="Filters"
  >
    <HiFunnel className="w-3.5 h-3.5" />
    {activeFilterCount > 0 && (
      <span className="absolute -top-1 -right-1 flex items-center justify-center w-3.5 h-3.5 rounded-full bg-cyan-500 text-white text-[8px] font-bold leading-none">
        {activeFilterCount}
      </span>
    )}
  </button>
);

// ── Main Header ────────────────────────────────────────────────────
const AutomationsHeader = ({
  automationsCount,
  summary,
  upcomingScheduled = [],
  timeRange,
  onTimeRangeChange,
  scheduledOnly,
  onScheduledOnlyChange,
  liveStats,
  searchQuery,
  onSearchChange,
  onCreateNew,
  activeFilterCount = 0,
  filterButtonRef,
  onToggleFilters,
}) => {
  return (
    <div className="px-1">
      {/* Main row */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-2 lg:mb-3">

        {/* Left: Title + Count + Create (mobile) */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
            Automations
          </h1>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums">
            {summary?.total_tasks ?? automationsCount}
          </span>
          {/* Mobile: create button pushed right */}
          <div className="md:hidden ml-auto">
            <CreateButton onCreateNew={onCreateNew} />
          </div>
        </div>

        {/* Center: Status Island (desktop only) */}
        <StatusIsland summary={summary} liveStats={liveStats} upcomingScheduled={upcomingScheduled} />

        {/* Right: Search + Controls + Create (desktop) */}
        <div className="flex items-center gap-2 md:ml-auto flex-wrap md:flex-nowrap">
          <SearchInput searchQuery={searchQuery} onSearchChange={onSearchChange} />
          <FilterButton activeFilterCount={activeFilterCount} filterButtonRef={filterButtonRef} onToggleFilters={onToggleFilters} />
          <TimelineControls
            timeRange={timeRange}
            onTimeRangeChange={onTimeRangeChange}
            scheduledOnly={scheduledOnly}
            onScheduledOnlyChange={onScheduledOnlyChange}
          />
          <div className="hidden md:flex items-center gap-2">
            <div className="h-4 w-px bg-gray-300 dark:bg-gray-700/50" />
            <CreateButton onCreateNew={onCreateNew} />
          </div>
        </div>
      </div>

      {/* Mobile: Status pills below controls */}
      <MobileStatusPills summary={summary} liveStats={liveStats} upcomingScheduled={upcomingScheduled} />
    </div>
  );
};

export default AutomationsHeader;
